import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Slider } from "../ui/slider";
import { Switch } from "../ui/switch";
import { Sparkles, Trash2, Loader2, Heart, BookOpen, Play, CircleDot, CircleCheck, Timer } from "lucide-react";
import { API, axios, ANSWER_STATUSES, answerStatusOf, sessionStatus, URGENCY_LEVELS } from "../../lib/planner";
import { toast } from "sonner";

const empty = {
  scripture: "", chapter: "", verse: "", prayer_point: "",
  time_of_prayer: "", duration_minutes: 15, notes: "", summary: "",
  completed: false, answered: false, answered_note: "",
  answer_status: "unanswered", started_at: null, urgency: "medium",
};

export const EntryDialog = ({ open, onOpenChange, context, entry, onSave, onDelete, onPrayNow }) => {
  const [form, setForm] = useState(empty);
  const [suggesting, setSuggesting] = useState(false);
  const [verse, setVerse] = useState(null);
  const [verseLoading, setVerseLoading] = useState(false);

  useEffect(() => {
    if (open) {
      const base = entry ? { ...empty, ...entry } : empty;
      base.answer_status = answerStatusOf(base);
      setForm(base);
      setVerse(null);
    }
  }, [open, entry]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchVerse = async () => {
    const ref = [form.scripture, form.chapter].filter(Boolean).join(" ") + (form.verse ? `:${form.verse}` : "");
    if (!ref.trim()) {
      toast.error("Enter a scripture and chapter first");
      return;
    }
    setVerseLoading(true);
    try {
      const res = await axios.get(`${API}/scripture`, { params: { reference: ref.trim() } });
      setVerse(res.data);
    } catch (e) {
      setVerse(null);
      toast.error("Could not find that verse");
    } finally {
      setVerseLoading(false);
    }
  };

  const suggestPrayerPoint = async () => {
    setSuggesting(true);
    try {
      const scriptureRef = [form.scripture, form.chapter, form.verse].filter(Boolean).join(" ");
      const res = await axios.post(`${API}/ai/prayer-point`, { scripture: scriptureRef, theme: "" });
      set("prayer_point", res.data.prayer_point);
      toast.success("Prayer point suggested");
    } catch (e) {
      toast.error("Could not fetch a suggestion");
    } finally {
      setSuggesting(false);
    }
  };

  const label = context ? `${context.day} · ${context.slotLabel}` : "";
  const sStatus = sessionStatus(form);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto rounded-2xl border-[#E5E1D8] bg-white p-8 sm:max-w-lg"
        data-testid="entry-dialog"
      >
        <DialogHeader>
          <p className="font-sans text-xs uppercase tracking-[0.15em] text-ink-muted">{label}</p>
          <DialogTitle className="font-serif text-3xl font-medium text-ink">
            {entry ? "Edit prayer" : "New prayer"}
          </DialogTitle>
          <DialogDescription className="sr-only">Record scripture, prayer point, time, duration, notes and summary</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1 space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Scripture</Label>
              <Input
                value={form.scripture}
                onChange={(e) => set("scripture", e.target.value)}
                placeholder="Psalm"
                className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
                data-testid="entry-scripture"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Chapter</Label>
              <Input
                value={form.chapter}
                onChange={(e) => set("chapter", e.target.value)}
                placeholder="23"
                className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
                data-testid="entry-chapter"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Verse</Label>
              <Input
                value={form.verse}
                onChange={(e) => set("verse", e.target.value)}
                placeholder="1-4"
                className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
                data-testid="entry-verse"
              />
            </div>
          </div>

          <div>
            <button
              type="button"
              onClick={fetchVerse}
              disabled={verseLoading}
              className="flex items-center gap-1.5 rounded-full bg-clay-soft px-3 py-1.5 text-xs font-medium text-brand transition-colors duration-300 hover:bg-[#e0d9c8] disabled:opacity-50"
              data-testid="fetch-verse-btn"
            >
              {verseLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <BookOpen className="h-3 w-3" />}
              Read this verse
            </button>
            {verse && (
              <div className="mt-3 rounded-lg border border-[#E5E1D8] bg-clay-panel p-4" data-testid="verse-box">
                <p className="mb-1 font-sans text-xs uppercase tracking-[0.1em] text-ink-muted">{verse.reference}</p>
                <p className="whitespace-pre-line font-serif text-lg italic leading-snug text-ink-soft">{verse.text}</p>
                <button
                  type="button"
                  onClick={() => { set("notes", form.notes ? `${form.notes}\n\n${verse.reference}: ${verse.text}` : `${verse.reference}: ${verse.text}`); toast.success("Verse added to notes"); }}
                  className="mt-2 text-xs font-medium text-brand hover:underline"
                  data-testid="insert-verse-btn"
                >
                  Add to notes
                </button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Prayer point</Label>
              <button
                type="button"
                onClick={suggestPrayerPoint}
                disabled={suggesting}
                className="flex items-center gap-1 rounded-full bg-clay-soft px-3 py-1 text-xs font-medium text-brand transition-colors duration-300 hover:bg-[#e0d9c8] disabled:opacity-50"
                data-testid="ai-suggest-btn"
              >
                {suggesting ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                Suggest
              </button>
            </div>
            <Textarea
              value={form.prayer_point}
              onChange={(e) => set("prayer_point", e.target.value)}
              placeholder="What are you praying for?"
              rows={2}
              className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
              data-testid="entry-prayer-point"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Time of prayer</Label>
              <Input
                type="time"
                value={form.time_of_prayer}
                onChange={(e) => set("time_of_prayer", e.target.value)}
                className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
                data-testid="entry-time"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">
                Duration · {form.duration_minutes} min
              </Label>
              <div className="pt-3">
                <Slider
                  value={[form.duration_minutes]}
                  onValueChange={(v) => set("duration_minutes", v[0])}
                  min={0}
                  max={120}
                  step={5}
                  data-testid="entry-duration"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Notes</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Reflections, requests, thoughts…"
              rows={2}
              className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
              data-testid="entry-notes"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-[0.1em] text-ink-muted">Summary</Label>
            <Textarea
              value={form.summary}
              onChange={(e) => set("summary", e.target.value)}
              placeholder="How did this time of prayer feel?"
              rows={2}
              className="rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
              data-testid="entry-summary"
            />
          </div>

          <div className="rounded-lg border border-[#E5E1D8] bg-white p-4" data-testid="session-block">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                {sStatus === "completed" ? (
                  <CircleCheck className="h-4 w-4 text-brand-success" />
                ) : sStatus === "in_progress" ? (
                  <Timer className="h-4 w-4 text-brand" />
                ) : (
                  <CircleDot className="h-4 w-4 text-ink-muted" />
                )}
                <div>
                  <p className="font-sans text-sm font-medium text-ink">Prayer session</p>
                  <p className="text-xs text-ink-muted" data-testid="session-status-text">
                    {sStatus === "completed"
                      ? "Completed"
                      : sStatus === "in_progress"
                      ? `In progress · started ${new Date(form.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
                      : "Prayer is set but has not yet started"}
                  </p>
                </div>
              </div>
              {sStatus === "not_started" && !form.completed && (
                <button
                  type="button"
                  onClick={() => set("started_at", new Date().toISOString())}
                  className="flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-medium text-white transition-colors duration-300 hover:bg-brand-hover"
                  data-testid="entry-start-btn"
                >
                  <Play className="h-3 w-3" /> Start now
                </button>
              )}
              {sStatus === "in_progress" && !form.completed && (
                <span className="rounded-full bg-clay-soft px-3 py-1 text-xs font-medium text-brand">Praying…</span>
              )}
            </div>
            {sStatus === "not_started" && (
              <p className="mt-3 font-serif text-base italic text-ink-soft" data-testid="entry-nudge">
                Set aside a quiet moment — He is waiting to meet with you.
              </p>
            )}
          </div>

          <div className="rounded-lg border border-[#E5E1D8] bg-white p-4">
            <p className="mb-2 font-sans text-sm font-medium text-ink">Urgency</p>
            <p className="mb-3 text-xs text-ink-muted">Used to prioritise this prayer on your Priorities page</p>
            <div className="grid grid-cols-3 gap-2">
              {URGENCY_LEVELS.map((u) => (
                <button
                  key={u.key}
                  type="button"
                  onClick={() => set("urgency", u.key)}
                  data-testid={`entry-urgency-${u.key}`}
                  className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 font-sans text-xs font-medium transition-colors duration-300 ${
                    form.urgency === u.key ? "border-transparent text-white" : "border-[#E5E1D8] bg-white text-ink-soft hover:bg-clay-panel"
                  }`}
                  style={form.urgency === u.key ? { backgroundColor: u.color } : {}}
                >
                  <span className="h-2 w-2 rounded-full" style={{ backgroundColor: form.urgency === u.key ? "#fff" : u.color }} />
                  {u.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg bg-clay-panel p-4">
            <div>
              <p className="font-sans text-sm font-medium text-ink">Mark as completed</p>
              <p className="text-xs text-ink-muted">Counts toward your weekly target</p>
            </div>
            <Switch
              checked={form.completed}
              onCheckedChange={(v) => set("completed", v)}
              data-testid="entry-completed"
            />
          </div>

          <div className="rounded-lg border border-[#E5E1D8] bg-white p-4">
            <div className="mb-3 flex items-center gap-2">
              <Heart className={`h-4 w-4 ${form.answer_status === "answered" ? "fill-[#C4A47C] text-[#C4A47C]" : "text-ink-muted"}`} />
              <div>
                <p className="font-sans text-sm font-medium text-ink">Prayer answer status</p>
                <p className="text-xs text-ink-muted">Is it in progress, awaiting, or answered?</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {ANSWER_STATUSES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => { set("answer_status", s.key); set("answered", s.key === "answered"); }}
                  data-testid={`answer-status-${s.key}`}
                  className={`rounded-lg border px-2 py-2 text-center font-sans text-xs font-medium transition-colors duration-300 ${
                    form.answer_status === s.key
                      ? "border-brand bg-clay-soft text-brand"
                      : "border-[#E5E1D8] bg-white text-ink-soft hover:bg-clay-panel"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {form.answer_status === "answered" && (
              <Textarea
                value={form.answered_note}
                onChange={(e) => set("answered_note", e.target.value)}
                placeholder="How did God answer this prayer?"
                rows={2}
                className="mt-3 rounded-lg border-[#E5E1D8] bg-clay-app focus-visible:ring-[#8FA388]"
                data-testid="entry-answered-note"
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex-row items-center justify-between sm:justify-between">
          {entry ? (
            <div className="flex items-center gap-2">
              <button
                onClick={() => onDelete(entry.id)}
                className="flex items-center gap-1 rounded-full px-3 py-2 text-sm text-destructive transition-colors duration-300 hover:bg-red-50"
                data-testid="entry-delete-btn"
              >
                <Trash2 className="h-4 w-4" /> Delete
              </button>
              <button
                onClick={() => onPrayNow(entry)}
                className="flex items-center gap-1 rounded-full bg-clay-soft px-3 py-2 text-sm font-medium text-brand transition-colors duration-300 hover:bg-[#e0d9c8]"
                data-testid="entry-pray-now-btn"
              >
                <Sparkles className="h-4 w-4" /> Pray now
              </button>
            </div>
          ) : <span />}
          <div className="flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-full" data-testid="entry-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={() => onSave(form)}
              className="rounded-full bg-brand text-white hover:bg-brand-hover"
              data-testid="entry-save-btn"
            >
              {entry ? "Save changes" : "Add prayer"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
